import type { ReactNode } from 'react';
import { Box, Card, TableContainer, useTheme, type SxProps, type Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ListHeader from './ListHeader';
import type { ListHeaderProps } from './ListHeader';

export interface FilteredListLayoutProps {
  headerProps?: ListHeaderProps;
  filterControls?: ReactNode;
  table: ReactNode;
  tableFooter?: ReactNode;
  errorContent?: ReactNode;
  filterCardSx?: SxProps<Theme>;
  tableCardSx?: SxProps<Theme>;
  tableContainerSx?: SxProps<Theme>;
}

export default function FilteredListLayout({
  headerProps,
  filterControls,
  table,
  tableFooter,
  errorContent,
  filterCardSx,
  tableCardSx,
  tableContainerSx,
}: FilteredListLayoutProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const cardShadow = isDarkMode ? '0 25px 45px rgba(0,0,0,0.65)' : '0 20px 45px rgba(0,0,0,0.12)';
  const filterBgColor = isDarkMode ? '#151521' : '#f5f4fb';

  const baseCardSx: SxProps<Theme> = {
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    borderRadius: 2,
    boxShadow: cardShadow,
    backgroundColor: theme.palette.background.paper,
  };

  return (
    <Box>
      {headerProps && <ListHeader {...headerProps} />}      
      {filterControls && (
        <Card sx={{ ...baseCardSx, mb: 3, ...filterCardSx }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'flex-start',
              px: 3,
              py: 3,
              backgroundColor: filterBgColor,
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            }}
          >
            {filterControls}
          </Box>
        </Card>
      )}
      {errorContent}
      <Card sx={{ ...baseCardSx, ...tableCardSx }}>
        <TableContainer
          sx={{
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            overflow: 'hidden',
            boxShadow: 'none',
            backgroundColor: theme.palette.background.paper,
            ...tableContainerSx,
          }}
        >
          {table}
        </TableContainer>
        {tableFooter}
      </Card>
    </Box>
  );
}
